import { inventoryAgeDays } from "./inventoryFacts";
import { conditionLabels, marketplaces } from "./labels";
import { money } from "./money";
import type {
  AnalyticsSummary,
  DashboardSummary,
  InventoryItem,
  InventoryItemInput,
  ListingDraft,
  Marketplace,
  MarketplaceAccount,
  PlatformPerformance,
  ProfitPoint,
  ReOSDatabase,
  Sale,
  StockAgeBucket
} from "./types";

export function currentUser(database: ReOSDatabase) {
  return database.users.find((user) => user.id === database.currentUserID);
}

export function activeWorkspace(database: ReOSDatabase) {
  return database.workspaces.find((workspace) => workspace.id === database.activeWorkspaceID);
}

export function workspaceItems(database: ReOSDatabase) {
  const workspaceID = database.activeWorkspaceID;
  return database.inventoryItems
    .filter((item) => item.workspaceID === workspaceID)
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
}

export function workspaceListings(database: ReOSDatabase) {
  const workspaceID = database.activeWorkspaceID;
  return database.marketplaceListings
    .filter((listing) => listing.workspaceID === workspaceID)
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
}

export function workspaceSales(database: ReOSDatabase) {
  const workspaceID = database.activeWorkspaceID;
  return database.sales.filter((sale) => sale.workspaceID === workspaceID).sort((a, b) => Date.parse(b.soldAt) - Date.parse(a.soldAt));
}

export function isRecognizedSale(sale: Sale) {
  return sale.status !== "cancelled" && (sale.status !== "refunded" || sale.refundAmount !== undefined);
}

export function workspaceNotifications(database: ReOSDatabase) {
  const workspaceID = database.activeWorkspaceID;
  return database.notifications
    .filter((notification) => notification.workspaceID === workspaceID)
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

export function workspaceAccounts(database: ReOSDatabase) {
  const workspaceID = database.activeWorkspaceID;
  return database.marketplaceAccounts
    .filter((account) => account.workspaceID === workspaceID)
    .sort((a, b) => a.marketplace.localeCompare(b.marketplace));
}

export function workspaceCategories(database: ReOSDatabase) {
  const workspaceID = database.activeWorkspaceID;
  return database.categories
    .filter((category) => category.workspaceID === workspaceID)
    .sort((a, b) => a.path.localeCompare(b.path));
}

export function itemFor(database: ReOSDatabase, itemID: string | undefined) {
  return database.inventoryItems.find((item) => item.id === itemID);
}

export function photosFor(database: ReOSDatabase, itemID: string | undefined) {
  return database.itemPhotos.filter((photo) => photo.itemID === itemID).sort((a, b) => a.sortOrder - b.sortOrder);
}

export function primaryPhoto(database: ReOSDatabase, itemID: string | undefined) {
  return photosFor(database, itemID)[0];
}

export function listingsFor(database: ReOSDatabase, itemID: string | undefined) {
  return workspaceListings(database).filter((listing) => listing.itemID === itemID);
}

export const GENERATED_SKU_MAX_LENGTH = 16;

export function generateSKU(database: ReOSDatabase, title: string, size?: string): string {
  const words = title
    .normalize("NFKD")
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter(Boolean);
  const sizePart = size?.normalize("NFKD").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "").slice(0, 4);
  const titlePart = words
    .slice(0, sizePart ? 1 : 2)
    .map((word) => word.slice(0, 4))
    .join("-") || "ITEM";
  const preferredBase = [titlePart, sizePart].filter(Boolean).join("-");
  const workspaceInventory = workspaceItems(database);
  const used = new Set(workspaceInventory.map((item) => item.sku.trim().toUpperCase()));
  const highestSequence = workspaceInventory.reduce((highest, item) => {
    const match = item.sku.trim().match(/-(\d+)$/);
    return match ? Math.max(highest, Number(match[1])) : highest;
  }, 0);
  let sequence = highestSequence + 1;
  while (true) {
    const suffix = String(sequence).padStart(4, "0");
    const availableBaseLength = Math.max(1, GENERATED_SKU_MAX_LENGTH - suffix.length - 1);
    const base = preferredBase.slice(0, availableBaseLength).replace(/-+$/, "") || "I";
    const candidate = `${base}-${suffix}`;
    if (!used.has(candidate)) return candidate;
    sequence += 1;
  }
}

export function dashboardSummary(database: ReOSDatabase, rangeDays = 30): DashboardSummary {
  const workspace = activeWorkspace(database);
  const currency = workspace?.defaultCurrency ?? "GBP";
  const now = new Date();
  const from = startOfDay(addDays(now, -Math.max(1, rangeDays) + 1));
  const to = endOfDay(now);
  const items = workspaceItems(database);
  const listings = workspaceListings(database);
  const sales = workspaceSales(database).filter(isRecognizedSale);
  const rangeSales = sales.filter((sale) => {
    const soldAt = new Date(sale.soldAt);
    return soldAt >= from && soldAt <= to;
  });
  const agingItems = items.filter((item) => item.status !== "sold" && item.status !== "archived" && (inventoryAgeDays(item) ?? -1) >= 60);

  return {
    activeListings: listings.filter((listing) => listing.status === "live").length,
    draftListings: listings.filter((listing) => ["draft", "ready", "failed"].includes(listing.status)).length,
    salesInRange: rangeSales.length,
    revenueInRange: money(
      rangeSales.reduce(
        (total, sale) => total + sale.salePrice.amountMinor + (sale.shippingCharged?.amountMinor ?? 0) - (sale.refundAmount?.amountMinor ?? 0),
        0
      ),
      currency
    ),
    netProfitInRange: money(
      rangeSales.reduce((total, sale) => total + sale.netProfit.amountMinor, 0),
      currency
    ),
    agingStockValue: money(
      agingItems.reduce((total, item) => total + item.purchaseCost.amountMinor, 0),
      currency
    ),
    agingStockItemCount: agingItems.length
  };
}

export function analyticsSummary(database: ReOSDatabase, rangeDays: number): AnalyticsSummary {
  const workspace = activeWorkspace(database);
  const currency = workspace?.defaultCurrency ?? "GBP";
  const from = startOfDay(addDays(new Date(), -rangeDays + 1));
  const items = workspaceItems(database);
  const workspaceSaleRecords = workspaceSales(database);
  const recognizedSaleItemIDs = new Set(workspaceSaleRecords.filter(isRecognizedSale).map((sale) => sale.itemID));
  const exceptionSaleItemIDs = new Set(workspaceSaleRecords.filter((sale) => !isRecognizedSale(sale)).map((sale) => sale.itemID));
  const sales = workspaceSaleRecords.filter((sale) => isRecognizedSale(sale) && new Date(sale.soldAt) >= from);
  const activeItems = items.filter((item) => item.status !== "sold" && item.status !== "archived");
  const soldItems = items.filter(
    (item) => item.status === "sold" && (!exceptionSaleItemIDs.has(item.id) || recognizedSaleItemIDs.has(item.id))
  ).length;

  const fees = sales.reduce((total, sale) => total + sale.marketplaceFees.amountMinor + (sale.paymentProcessingFees?.amountMinor ?? 0), 0);
  const shippingCosts = sales.reduce((total, sale) => total + (sale.shippingCost?.amountMinor ?? 0), 0);
  const packagingCosts = sales.reduce((total, sale) => total + (sale.packagingCost?.amountMinor ?? 0), 0);

  return {
    revenue: money(
      sales.reduce(
        (total, sale) => total + sale.salePrice.amountMinor + (sale.shippingCharged?.amountMinor ?? 0) - (sale.refundAmount?.amountMinor ?? 0),
        0
      ),
      currency
    ),
    fees: money(fees, currency),
    purchaseCosts: money(
      sales.reduce((total, sale) => total + sale.purchaseCost.amountMinor, 0),
      currency
    ),
    shippingCosts: money(shippingCosts, currency),
    packagingCosts: money(packagingCosts, currency),
    netProfit: money(
      sales.reduce((total, sale) => total + sale.netProfit.amountMinor, 0),
      currency
    ),
    sellThroughRate: items.length === 0 ? 0 : soldItems / items.length,
    averageAgeOfStockDays:
      activeItems.filter((item) => inventoryAgeDays(item) !== undefined).length === 0
        ? 0
        : activeItems.reduce((total, item) => total + (inventoryAgeDays(item) ?? 0), 0) / activeItems.filter((item) => inventoryAgeDays(item) !== undefined).length,
    activeInventoryValue: money(
      activeItems.reduce((total, item) => total + item.purchaseCost.amountMinor, 0),
      currency
    )
  };
}

export function profitOverTime(database: ReOSDatabase, rangeDays: number): ProfitPoint[] {
  const workspace = activeWorkspace(database);
  const currency = workspace?.defaultCurrency ?? "GBP";
  const today = startOfDay(new Date());
  const sales = workspaceSales(database).filter(isRecognizedSale);

  return Array.from({ length: rangeDays }).map((_, index) => {
    const date = addDays(today, index - rangeDays + 1);
    const total = sales
      .filter((sale) => isSameDay(new Date(sale.soldAt), date))
      .reduce((sum, sale) => sum + sale.netProfit.amountMinor, 0);
    return {
      date: date.toISOString(),
      netProfit: money(total, currency)
    };
  });
}

export function stockAgeBuckets(database: ReOSDatabase): StockAgeBucket[] {
  const items = workspaceItems(database).filter((item) => item.status !== "sold" && item.status !== "archived");
  const buckets: StockAgeBucket[] = [
    { label: "0-30", lowerBoundDays: 0, upperBoundDays: 30, count: 0 },
    { label: "31-60", lowerBoundDays: 31, upperBoundDays: 60, count: 0 },
    { label: "61-90", lowerBoundDays: 61, upperBoundDays: 90, count: 0 },
    { label: "90+", lowerBoundDays: 91, count: 0 }
  ];

  items.forEach((item) => {
    const days = (inventoryAgeDays(item) ?? -1);
    const bucket = buckets.find((candidate) => days >= candidate.lowerBoundDays && (candidate.upperBoundDays === undefined || days <= candidate.upperBoundDays));
    if (bucket) {
      bucket.count += 1;
    }
  });

  return buckets;
}

export function platformPerformance(database: ReOSDatabase, rangeDays: number): PlatformPerformance[] {
  const from = startOfDay(addDays(new Date(), -rangeDays + 1));
  const listings = workspaceListings(database);
  const sales = workspaceSales(database).filter((sale) => isRecognizedSale(sale) && new Date(sale.soldAt) >= from);
  const workspace = activeWorkspace(database);
  const currency = workspace?.defaultCurrency ?? "GBP";

  return marketplaces.map((marketplace) => ({
    marketplace,
    soldCount: sales.filter((sale) => sale.marketplace === marketplace).length,
    liveListingCount: listings.filter((listing) => listing.marketplace === marketplace && listing.status === "live").length,
    netProfit: money(
      sales.filter((sale) => sale.marketplace === marketplace).reduce((total, sale) => total + sale.netProfit.amountMinor, 0),
      currency
    )
  }));
}

export function agingStockItems(database: ReOSDatabase): InventoryItem[] {
  return workspaceItems(database).filter((item) => item.status !== "sold" && item.status !== "archived" && (inventoryAgeDays(item) ?? -1) >= 60);
}

export function previewListingDrafts(
  input: InventoryItemInput,
  photoCount: number,
  selectedMarketplaces: Marketplace[],
  accounts: MarketplaceAccount[],
  currency = "GBP"
): ListingDraft[] {
  const tempItem: InventoryItem = {
    id: "preview",
    workspaceID: "preview",
    title: input.title.trim(),
    description: descriptionFromInput(input, photoCount),
    categoryID: input.categoryID.trim(),
    condition: input.condition,
    brand: input.brand.trim() || undefined,
    size: input.size.trim() || undefined,
    color: input.color.trim() || undefined,
    material: input.material.trim() || undefined,
    sku: input.sku.trim(),
    stockLocation: input.stockLocation.trim() || undefined,
    purchaseCost: money(input.purchaseCostMinor, currency),
    purchaseCostStatus: input.purchaseCostStatus,
    targetPrice: money(input.targetPriceMinor, currency),
    minimumPrice: input.minimumPriceMinor === undefined ? undefined : money(input.minimumPriceMinor, currency),
    quantity: input.quantity,
    status: "draft",
    notes: input.notes.trim() || undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  return listingDraftsForItem(tempItem, photoCount, selectedMarketplaces, accounts);
}

export function listingDraftsForItem(
  item: InventoryItem,
  photoCount: number,
  selectedMarketplaces: Marketplace[],
  accounts: MarketplaceAccount[]
): ListingDraft[] {
  return selectedMarketplaces.map((marketplace) => {
    const warnings = validationWarnings(item, marketplace, photoCount, accounts.find((account) => account.marketplace === marketplace));
    return {
      marketplace,
      title: marketplaceTitle(item, marketplace),
      description: marketplaceDescription(item, marketplace),
      price: item.targetPrice,
      shippingPrice: marketplace === "etsy" || marketplace === "ebay" ? money(499, item.targetPrice.currency) : undefined,
      categoryExternalID: item.categoryID.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      conditionExternalValue: item.condition,
      validationWarnings: warnings
    };
  });
}

export function calculateNetProfit(input: {
  salePriceMinor: number;
  shippingChargedMinor: number;
  purchaseCostMinor: number;
  marketplaceFeesMinor: number;
  processingFeesMinor: number;
  shippingCostMinor: number;
  packagingCostMinor: number;
  currency?: string;
}) {
  return money(
    input.salePriceMinor +
      input.shippingChargedMinor -
      input.purchaseCostMinor -
      input.marketplaceFeesMinor -
      input.processingFeesMinor -
      input.shippingCostMinor -
      input.packagingCostMinor,
    input.currency ?? "GBP"
  );
}

export function ageDays(dateString: string, now = new Date()): number {
  const diff = now.getTime() - Date.parse(dateString);
  return Math.max(0, Math.floor(diff / 86_400_000));
}

export function descriptionFromInput(input: InventoryItemInput, _photoCount = 0): string {
  const title = input.title.trim();
  const brand = input.brand.trim();
  return [productLeadSentence(title, brand), input.notes.trim()].filter(Boolean).join("\n");
}

export function marketplaceTitle(item: InventoryItem, _marketplace: Marketplace): string {
  return item.title.trim();
}

export function marketplaceDescription(item: InventoryItem, _marketplace: Marketplace): string {
  return [
    item.description.trim(),
    marketplaceDetailsLine(item)
  ].filter(Boolean).join("\n");
}

export function cleanSellerListingDescription(description: string, fallbackTitle = "Imported listing"): string {
  const cleaned = description
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !/^Brand\s*:/i.test(line))
    .filter((line) => !/^Condition\s*:/i.test(line))
    .filter((line) => !/ReOS|assisted manual|runtime|backend/i.test(line))
    .join("\n")
    .trim();

  if (cleaned) {
    return cleaned;
  }

  const title = fallbackTitle.trim() || "Imported listing";
  return `${title}.\nReview details before listing.`;
}

function productLeadSentence(title: string, brand?: string) {
  const cleanedTitle = title.trim();
  const cleanedBrand = brand?.trim();
  if (!cleanedBrand) {
    return sentenceFor(cleanedTitle);
  }
  return startsWithBrand(cleanedTitle, cleanedBrand) ? sentenceFor(cleanedTitle) : `${cleanedTitle} from ${cleanedBrand}.`;
}

function marketplaceDetailsLine(item: InventoryItem) {
  const facts = [
    item.size ? `Size ${item.size}` : undefined,
    item.color ? item.color : undefined,
    item.material ? item.material : undefined,
    `${humanizeCondition(item.condition)} condition`
  ].filter(Boolean);
  return facts.length ? `Details: ${facts.join(" / ")}.` : undefined;
}

function sentenceFor(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function startsWithBrand(title: string, brand: string) {
  const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const normalizedTitle = normalize(title);
  const normalizedBrand = normalize(brand);
  return normalizedTitle === normalizedBrand || normalizedTitle.startsWith(`${normalizedBrand} `);
}

function humanizeCondition(condition: InventoryItem["condition"]) {
  return conditionLabels[condition];
}

function validationWarnings(item: InventoryItem, marketplace: Marketplace, photoCount: number, account?: MarketplaceAccount): string[] {
  const warnings: string[] = [];
  if (!item.title.trim()) {
    warnings.push("Title is required.");
  }
  if (!item.categoryID.trim()) {
    warnings.push("Category is required.");
  }
  if (item.condition === "unknown") warnings.push("Check the item's condition before preparing a listing.");
  if (photoCount === 0) {
    warnings.push("Add at least one product photo before preparing a listing draft.");
  }
  if (marketplace === "etsy" && photoCount < 5) {
    warnings.push("Etsy performs better with at least 5 photos.");
  }
  if (marketplace === "ebay" && !item.brand) {
    warnings.push("eBay buyers often filter by brand.");
  }
  if (account?.connectionStatus === "error") {
    warnings.push("Marketplace account needs attention.");
  }
  return warnings;
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function endOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy;
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
